import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
  ErrorResponse,
  WatcherOperation,
  createWatcherData,
  ThreadContextFields,
  TimestampsFields,
} from "./common";
const MessageRole = z.enum(["guest", "registered", "assistant", "system"]);

const TagReference = z.object({
  tagId: z.string(),
  kind: z.string(), // i.e. image, video, file, participant, message, ...
  type: z.string(), // i.e. mime type
  description: z.string(),
});

const ReasoningMetadata = z
  .object({ steps: z.array(z.string()), conclusion: z.string() })
  .partial();
const ReferencesMetadata = z.array(TagReference);
const MessageMetadata = z
  .object({
    model: z.string(),
    tokensUsed: z.number().int(),
    responseTime: z.number(),
  })
  .partial();

const SendMessageRequest = z.object({
  content: z.string().min(1),
  role: MessageRole.optional().default("guest"),
  threadId: UuidField,
  conversationId: UuidField,
  workspaceId: UuidField,
  participantId: ParticipantIdField, // Client-generated device fingerprint hash (required)
  reasoning: ReasoningMetadata.optional(), // Changed from string to ReasoningMetadata for consistency
  references: ReferencesMetadata.optional(),
  metadata: MessageMetadata.optional(),
});

const GetMessagesRequest = z.object({
  workspaceId: UuidField,
  conversationId: UuidField,
  threadId: UuidField,
  limit: z.number().int().min(1).max(100).optional().default(50),
  before: z.iso.datetime().optional(),
});

const GetConversationsRequest = z.object({
  workspaceId: UuidField,
  limit: z.number().int().min(1).max(100).optional().default(20),
});

const GetChatCacheRequest = z.object({
  workspaceId: UuidField,
});

const SubscribeToMessagesRequest = z.object({
  workspaceId: UuidField,
  conversationId: UuidField,
  threadId: UuidField,
  historyLimit: z.number().int().min(1).max(100).optional().default(10),
});

const ChatMessage = z.object({
  id: UuidField,
  originId: z.string().optional(),
  content: z.string(),
  role: MessageRole,
  timestamp: TimestampField,
  threadId: UuidField,
  conversationId: UuidField,
  workspaceId: UuidField,
  participantId: ParticipantIdField, // Client-generated device fingerprint hash
  isStreaming: z.boolean().optional().default(false),
  status: z
    .enum(["sending", "sent", "delivered", "failed", "pending"])
    .optional()
    .default("sent"),
  reasoning: ReasoningMetadata.optional(),
  references: ReferencesMetadata.optional(),
  metadata: MessageMetadata.optional(),
});

const MessageListResponse = z.object({
  messages: z.array(ChatMessage),
  hasMore: z.boolean(),
});

const CreateConversationRequest = z.object({
  title: z.string().min(1),
  workspaceId: UuidField,
  model: z.string().optional(),
  systemPrompt: z.string().optional(),
});

const ChatConversation = z.object({
  id: UuidField,
  title: z.string(),
  workspaceId: UuidField,
  ...TimestampsFields,
  isActive: z.boolean(),
  model: z.string().optional(),
  systemPrompt: z.string().optional(),
  pendingMessages: z.number().int(),
  draftText: z.string().optional(),
});

const CreateThreadRequest = z.object({
  conversationId: UuidField,
  title: z.string().optional(),
});

const ChatThread = z.object({
  id: UuidField,
  conversationId: UuidField,
  title: z.string().optional(),
  ...TimestampsFields,
  isActive: z.boolean(),
});

const ConversationListResponse = z.object({
  conversations: z.array(ChatConversation),
  total: z.number().int(),
});

const ChatCacheResponse = z.object({
  workspaceId: UuidField,
  conversations: z.record(
    z.string(),
    z.object({ threads: z.record(z.string(), z.array(UuidField)) }).partial(),
  ),
  updatedAt: TimestampField,
});

// Watcher callback data structures
const ConversationWatchData = createWatcherData(ChatConversation);
const ThreadWatchData = createWatcherData(ChatThread);

// Composition schemas for message composer
const Composition = z.object({
  participantId: ParticipantIdField, // Client-generated device fingerprint hash
  content: z.string(),
  ...ThreadContextFields,
  updatedAt: TimestampField,
  reasoning: ReasoningMetadata.optional(),
  references: ReferencesMetadata.optional(),
  metadata: MessageMetadata.optional(),
});

const ComposingEvent = z.object({
  participantId: ParticipantIdField,
  ...ThreadContextFields,
  content: z.string(),
  reasoning: ReasoningMetadata.optional(),
  references: ReferencesMetadata.optional(),
  metadata: MessageMetadata.optional(),
  timestamp: TimestampField,
});

const TypingEvent = z.object({
  participantId: ParticipantIdField,
  ...ThreadContextFields,
  timestamp: TimestampField,
});

// Messenger schemas for thread-based messaging
const Thread = z.object({
  ...ThreadContextFields,
  ...TimestampsFields,
});

const ThreadUpdateEvent = z.object({
  ...ThreadContextFields,
  messageIds: z.array(UuidField).min(1),
  timestamp: TimestampField,
});

const MessageRead = z.object({
  participantId: ParticipantIdField,
  messageId: UuidField,
  threadId: UuidField,
  firstReadAt: TimestampField,
  lastReadAt: TimestampField,
});

const MessageReply = z.object({
  replyMessageId: UuidField,
  originalMessageId: UuidField,
  participantId: ParticipantIdField,
  threadId: UuidField,
  timestamp: TimestampField,
});

const MessageReaction = z.object({
  participantId: ParticipantIdField,
  messageId: UuidField,
  threadId: UuidField,
  emoji: z.string().emoji(),
  timestamp: TimestampField,
});

// Event schemas for messenger operations
const ParticipantUpdateEvent = z.object({
  threadId: UuidField,
  participantId: ParticipantIdField,
  action: z.enum(["added", "removed"]),
  timestamp: TimestampField,
});

const MessageReadEvent = z.object({
  read: MessageRead,
  timestamp: TimestampField,
});

const MessageReactionEvent = z.object({
  reaction: MessageReaction,
  action: z.enum(["added", "updated", "removed"]),
  timestamp: TimestampField,
});

// Reader schemas for message scrolling and caching
const Reader = z.object({
  participantId: ParticipantIdField, // Client-generated device fingerprint hash
  ...ThreadContextFields,

  // Cache boundaries
  cacheStartPosition: z.number().int().gte(0),
  cacheEndPosition: z.number().int().gte(0),
  totalMessages: z.number().int().gte(0),

  // Current window
  currentPosition: z.number().int().gte(0),
  windowSize: z.number().int().min(1).max(256).default(128),
  cacheSize: z.number().int().min(16).default(1024),

  // Metadata
  updatedAt: TimestampField,
});

const ReaderEvent = z.object({
  participantId: ParticipantIdField,
  ...ThreadContextFields,
  action: z.enum([
    "scroll_up",
    "scroll_down",
    "scroll_to_end",
    "cache_refresh",
  ]),
  oldPosition: z.number().int().gte(0),
  newPosition: z.number().int().gte(0),
  cacheUpdated: z.boolean(),
  timestamp: TimestampField,
});

const ReaderWindow = z.object({
  participantId: ParticipantIdField,
  threadId: UuidField,
  messages: z.array(ChatMessage),
  windowStart: z.number().int().gte(0),
  windowEnd: z.number().int().gte(0),
  totalMessages: z.number().int().gte(0),
  hasMore: z.boolean(),
});

export const schemas = {
  MessageRole,
  TagReference,
  ReasoningMetadata,
  ReferencesMetadata,
  MessageMetadata,
  SendMessageRequest,
  GetMessagesRequest,
  GetConversationsRequest,
  GetChatCacheRequest,
  SubscribeToMessagesRequest,
  ChatMessage,
  ErrorResponse,
  MessageListResponse,
  CreateConversationRequest,
  ChatConversation,
  CreateThreadRequest,
  ChatThread,
  ConversationListResponse,
  ChatCacheResponse,
  WatcherOperation,
  ConversationWatchData,
  ThreadWatchData,
  Composition,
  ComposingEvent,
  TypingEvent,
  Thread,
  ThreadUpdateEvent,
  MessageRead,
  MessageReply,
  MessageReaction,
  ParticipantUpdateEvent,
  MessageReadEvent,
  MessageReactionEvent,
  Reader,
  ReaderEvent,
  ReaderWindow,
};

// oRPC Type Inference Support
// These types enable proper oRPC client generation and type inference

export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenient access
export type * from "zod";
